/**
 * PaginaAnalisePeticaoInicial - Fluxo Completo de Análise Processual
 * 
 * CONTEXTO DE NEGÓCIO:
 * Página dedicada para a Análise Processual com fluxo guiado em wizard.
 * Este fluxo foi projetado para guiar o usuário por etapas sequenciais até
 * a geração de minuta, pareceres e prognóstico.
 * 
 * FUNCIONALIDADES:
 * 1. Upload do processo/ documento (PDF/DOCX)
 * 2. Exibição de documentos sugeridos pela LLM
 * 3. Upload de documentos complementares
 * 4. Seleção de agentes (advogados especialistas + peritos)
 * 5. Processamento assíncrono com atualizações em tempo real via WebSocket
 * 6. Exibição de resultados com foco na Minuta (Edição) como artefato principal
 * 
 * RESPONSABILIDADES:
 * - Coordenar fluxo em wizard (6 etapas)
 * - Gerenciar estado global da análise processual
 * - Validar cada etapa antes de avançar
 * - Receber atualizações de progresso via WebSocket (TAREFA-095)
 * - Exibir resultados estruturados
 * 
 * NOTA PARA LLMs:
 * Este é o componente principal da TAREFA-049. Todos os subcomponentes
 * foram implementados nas tarefas seguintes (050-056) e estão integrados.
 * A integração com WebSocket foi adicionada na TAREFA-095, eliminando
 * a necessidade de polling.
 * 
 * SUBCOMPONENTES (Etapa 6 - Resultados):
 * - TAREFA-053: Próximos Passos (ComponenteProximosPassos) ✅
 * - TAREFA-054: Gráfico de Prognóstico (ComponenteGraficoPrognostico) ✅
 * - TAREFA-055: Pareceres Individualizados (ComponentePareceresIndividualizados) ✅
 * - TAREFA-102: Minuta (Edição) integrada no Acordeon 4 como artefato principal ✅
 * - TAREFA-095: Integração WebSocket para atualizações em tempo real ✅
 * - TAREFA-106: Atualização de rótulos e foco em Minuta (Edição) ✅
 */
/**
 * Componente da página de Análise Processual (wizard principal)
 * 
 * IMPLEMENTAÇÃO:
 * - State management com useState (etapa atual, dados de cada etapa)
 * - Navegação entre etapas com validação
 * - Atualizações em tempo real via WebSocket (TAREFA-095)
 * - Renderização condicional por etapa
 */
import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { FileText, CheckCircle2, Users, BarChart3, FileCheck, Scale } from 'lucide-react';
import type {
  DocumentoSugerido,
  AgentesSelecionados,
  ResultadoAnaliseProcesso,
} from '../tipos/tiposPeticao';

import { ComponenteUploadPeticaoInicial, type ArquivoIgnorado } from '../componentes/peticao/ComponenteUploadPeticaoInicial';
import { ComponenteDocumentosSugeridos } from '../componentes/peticao/ComponenteDocumentosSugeridos';
import { ComponenteSelecaoAgentesPeticao } from '../componentes/peticao/ComponenteSelecaoAgentesPeticao';
import { ComponenteUploadSindicaisJurisprudencia } from '../componentes/peticao/ComponenteUploadSindicaisJurisprudencia';
import { ResumoPreAnalise } from '../componentes/ResumoPreAnalise';
import { ComponenteProximosPassos } from '../componentes/peticao/ComponenteProximosPassos';
import { ComponenteGraficoPrognostico } from '../componentes/peticao/ComponenteGraficoPrognostico';
import { ComponentePareceresIndividualizados } from '../componentes/peticao/ComponentePareceresIndividualizados';
import { ComponenteEditorMinuta } from '../componentes/peticao/ComponenteEditorMinuta';
import SeletorCenarios from '../componentes/minutas/SeletorCenarios';
import { ComponenteChatIA } from '../componentes/chat/ComponenteChatIA';
import { iniciarAnalise, obterResultadoAnalise, cancelarAnalise, listarDocumentosPeticao } from '../servicos/servicoApiPeticoes';
import { criarProcesso, obterProcesso } from '../servicos/servicoApiProcessos';
import { iniciarPollingStatus } from '../servicos/servicoApiAnalise';
import { obterWebSocketService } from '../../../core/servicos/websocketService';
import type { AtualizacaoStatusAnalise } from '../../../core/servicos/websocketService';
import { useAutenticacao } from '../../../core/contextos/ContextoAutenticacao';
import { renovarToken } from '../../../core/servicos/servicoApiAutenticacao';
import { LOCALSTORAGE_REFRESH_TOKEN_KEY, LOCALSTORAGE_TOKEN_KEY } from '../../../core/contextos/authConstants';
import Accordion from '../../../core/componentes/comuns/Accordion';

// ===== TIPOS LOCAIS =====

/**
 * Etapas do wizard
 */
type EtapaWizard = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Status de cada etapa
 * - pending: Ainda não iniciada
 * - in-progress: Etapa atual
 * - completed: Concluída com sucesso
 * - error: Erro nesta etapa
 */
type StatusEtapa = 'pending' | 'in-progress' | 'completed' | 'error';

/**
 * Metadata de uma etapa
 */
interface MetadataEtapa {
  numero: EtapaWizard;
  titulo: string;
  descricao: string;
  icone: React.ComponentType<{ className?: string }>;
  status: StatusEtapa;
}

// ===== COMPONENTE PRINCIPAL =====

/**
 * Componente da página de Análise Processual (wizard principal)
 * 
 * IMPLEMENTAÇÃO:
 * - State management com useState (etapa atual, dados de cada etapa)
 * - Navegação entre etapas com validação
 * - Atualizações em tempo real via WebSocket (TAREFA-095)
 * - Renderização condicional por etapa
 */
export function AnalisePeticaoInicial(): JSX.Element {
  const { token } = useAutenticacao();
  const tokenRef = useRef(token);

  // Atualizar ref quando token mudar
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // ===== STATE =====

  /**
   * Etapa atual do wizard (1-6)
   */
  const [etapaAtual, setEtapaAtual] = useState<EtapaWizard>(1);

  /**
   * ID da petição (documento enviado) criada no backend.
   * OBS: o nome da variável (`peticaoId`) é mantido para compatibilidade com
   * outros componentes e serviços; aqui a string representa o documento que
   * alimenta a Análise Processual.
   */
  const [peticaoId, setPeticaoId] = useState<string>('');

  /**
   * ID do processo (persistido no backend) que representa a sessão de análise.
  * - É diferente do `peticaoId` (que representa o documento enviado).
   * - Salvamos `processoId` em `localStorage` como chave `processo_id_ativo`.
   */
  const [processoId, setProcessoId] = useState<string>('');

  /**
   * Flag que indica que devemos apenas reconectar ao WebSocket (o backend
   * já iniciou o processamento) em vez de reiniciar a análise via POST.
   * Usado quando re-hidratamos uma sessão em estado 'processing'.
   */
  const [retomarSomentePolling, setRetomarSomentePolling] = useState<boolean>(false);

  // Hook para acessar query params (?processo_id=...)
  const location = useLocation();

  /**
   * ID do upload do processo/documento (rastreamento do upload)
   */
  const [uploadId, setUploadId] = useState<string>('');

  /**
   * Tipo de ação jurídica (inferido ou informado pelo usuário)
   */
  // Removido: tipoAcao não é utilizado fora do callback

  /**
   * Documentos sugeridos pela LLM
   */
  const [documentosSugeridos, setDocumentosSugeridos] = useState<DocumentoSugerido[]>([]);

  /**
   * IDs dos documentos complementares enviados
   */
  const [documentosEnviados, setDocumentosEnviados] = useState<string[]>([]);

  /**
   * Agentes selecionados para análise
   */
  const [agentesSelecionados, setAgentesSelecionados] = useState<AgentesSelecionados>({
    advogados: [],
    peritos: [],
  });
  // Ref to store initial metadados loaded from processo to avoid re-writing identical data
  type MetadadosProcesso = { documentos_enviados?: string[]; agentes_selecionados?: AgentesSelecionados;[k: string]: unknown };
  const metadadosIniciaisRef = useRef<MetadadosProcesso | null>(null);

  /**
   * Progresso da análise (0-100)
   */
  const [progressoAnalise, setProgressoAnalise] = useState<number>(0);

  /**
   * Etapa atual da análise (descrição textual)
   */
  const [etapaAnalise, setEtapaAnalise] = useState<string>('');

  /**
   * Resultado completo da análise
   */
  const [resultado, setResultado] = useState<ResultadoAnaliseProcesso | null>(null);

  /**
   * Lista de arquivos ignorados por tipo não suportado (do upload inicial)
   */
  const [arquivosIgnorados, setArquivosIgnorados] = useState<ArquivoIgnorado[]>([]);

  /**
   * Mensagem de erro (se houver)
   */
  const [erro, setErro] = useState<string>('');

  /**
   * Flag que indica se a página foi aberta com ?processo_id= (vindo do Dashboard)
   * Quando true, permitimos re-hidratar o processo passado na query. Quando
   * false (acesso direto), sempre iniciamos um novo processo e NÃO usamos
   * localStorage para re-hidratação automática.
   */
  // Removido: entrouComQuery não é utilizado fora do efeito de re-hidratação

  /**
   * Efeito de montagem: tenta re-hidratar sessão a partir de query param
   * `processo_id` ou do `localStorage` (chave: `processo_id_ativo`). Caso
   * identifique um processo com estado 'processing', ajusta flags para que
   * a página pule para a etapa de processamento e reconecte ao WebSocket.
   */
  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams(location.search);
        const queryProcessoId = params.get('processo_id');

        // Only rehydrate when a processo_id is explicitly passed in the URL
        if (!queryProcessoId) return; // Nothing to rehydrate
        // mark that the page was opened with a query param (coming from dashboard)
        // (flag removida por não uso)

        const idToUse = queryProcessoId;

        console.log('[AnalisePeticaoInicial] Tentando re-hidratar processo:', idToUse);

        const resp = await obterProcesso(idToUse);
        const processo = resp.data;

        // Salva/processa id localmente
        const pid = (processo as unknown as { processo_id?: string; id?: string }).processo_id || (processo as unknown as { processo_id?: string; id?: string }).id || idToUse;
        setProcessoId(pid);

        // Re-hidrata metadados (documentos, agentes, peticao_id)
        const meta = (processo.metadados || {}) as Record<string, unknown>;
        metadadosIniciaisRef.current = meta;
        if (meta.documentos_sugeridos) setDocumentosSugeridos(meta.documentos_sugeridos as DocumentoSugerido[]);
        if (meta.documentos_enviados) setDocumentosEnviados(meta.documentos_enviados as string[]);
        if (meta.agentes_selecionados) setAgentesSelecionados(meta.agentes_selecionados as AgentesSelecionados);
        if (meta.peticao_id) setPeticaoId(meta.peticao_id as string);

        // Ajusta o step com base em estado_wizard (mapear para números do wizard)
        const estadoWizard = (processo.estado_wizard || (meta as { estado_wizard?: string }).estado_wizard || '') as string;
        const mapEstadoParaEtapa = (estado: string): EtapaWizard => {
          const normalizado = (estado || '').toLowerCase();
          if (normalizado.includes('upload')) return 1;
          if (normalizado.includes('complementar')) return 2;
          if (normalizado.includes('sindical') || normalizado.includes('jurispr')) return 3;
          if (normalizado.includes('agente') || normalizado.includes('selecion')) return 4;
          if (normalizado.includes('process')) return 5;
          if (normalizado.includes('done') || normalizado.includes('concl')) return 6;
          return 1;
        };

        const etapa = mapEstadoParaEtapa(estadoWizard as string);
        setEtapaAtual(etapa);

        // Sempre que re-hidratamos, se o processo já contém resultado persistido
        // ou se status_processamento === 'done', pular diretamente para a tela de Resultados.
        try {
          const resultadoPersistido = (processo.metadados || {}).resultado_analise;
          const statusProc = (processo.status_processamento || '').toString().toLowerCase();
          if (resultadoPersistido || statusProc === 'done' || statusProc === 'concluido' || statusProc === 'concluded') {
            // Aplicar resultado (se disponível) e pular para etapa 6
            if (resultadoPersistido) {
              try {
                setResultado(resultadoPersistido as ResultadoAnaliseProcesso);
                setEtapaAtual(6);
                console.log('[AnalisePeticaoInicial] Resultado carregado do processo (metadados)');
                return;
              } catch (err) {
                console.warn('[AnalisePeticaoInicial] Falha ao aplicar resultado persistido:', err);
              }
            }

            // Se status indica done mas não há resultado em metadados, tentar obter via API de processo/petição
            const petId = (meta as { peticao_id?: string }).peticao_id;
            if (petId) {
              try {
                const respRes = await obterResultadoAnalise(petId as string);
                if (respRes && respRes.data) {
                  setResultado(respRes.data as ResultadoAnaliseProcesso);
                  setEtapaAtual(6);
                  console.log('[AnalisePeticaoInicial] Resultado obtido via API de processo/petição e exibido');
                  return;
                }
              } catch (err) {
                console.warn('[AnalisePeticaoInicial] Não foi possível obter resultado via API de processo/petição:', err);
              }
            }
          }
        } catch (err) {
          console.warn('[AnalisePeticaoInicial] Erro ao verificar resultado persistido:', err);
        }

        // Se estiver em processamento, instruir para reconectar ao WebSocket apenas
        if ((processo.status_processamento || '').toLowerCase() === 'processing') {
          setRetomarSomentePolling(true);
          setEtapaAtual(5); // pular para tela de progresso
          console.log('[AnalisePeticaoInicial] Processo em processing — reconectando ao WebSocket');
        }
      } catch (err) {
        console.warn('[AnalisePeticaoInicial] Falha ao re-hidratar processo (ignorado):', err);
      }
    })();
  }, [location.search]);

  /**
   * Efeito de montagem: verifica sessionStorage para reconexão após refresh
   * 
   * CONTEXTO: Quando o usuário recarrega a página (F5), verificamos se há
   * um upload_id armazenado no sessionStorage. Se existir, tentamos reconectar
   * ao WebSocket para continuar acompanhando o processo em andamento.
   */
  useEffect(() => {
    const uploadId = sessionStorage.getItem('upload_id');
    if (uploadId) {
      console.log('[AnalisePeticaoInicial] Upload ID encontrado no sessionStorage, tentando reconectar:', uploadId);
      setPeticaoId(uploadId);
      setRetomarSomentePolling(true);
      setEtapaAtual(5); // Ir para etapa de processamento
    }
  }, []);

  // ===== METADATA DAS ETAPAS =====

  const etapas: MetadataEtapa[] = [
    {
      numero: 1,
      titulo: 'Upload do Processo',
      descricao: 'Envie o processo (documento) para análise',
      icone: FileText,
      status: etapaAtual === 1 ? 'in-progress' : etapaAtual > 1 ? 'completed' : 'pending',
    },
    {
      numero: 2,
      titulo: 'Documentos Complementares',
      descricao: 'Veja documentos sugeridos e faça upload',
      icone: FileCheck,
      status: etapaAtual === 2 ? 'in-progress' : etapaAtual > 2 ? 'completed' : 'pending',
    },
    {
      numero: 3,
      titulo: 'Documentos Sindicais & Jurisprudência',
      descricao: 'Faça upload de convenções sindicais e trechos de jurisprudência (opcional)',
      icone: FileCheck,
      status: etapaAtual === 3 ? 'in-progress' : etapaAtual > 3 ? 'completed' : 'pending',
    },
    {
      numero: 4,
      titulo: 'Seleção de Agentes',
      descricao: 'Escolha advogados e peritos',
      icone: Users,
      status: etapaAtual === 4 ? 'in-progress' : etapaAtual > 4 ? 'completed' : 'pending',
    },
    {
      numero: 5,
      titulo: 'Processamento',
      descricao: 'Análise multi-agent em andamento',
      icone: BarChart3,
      status: etapaAtual === 5 ? 'in-progress' : etapaAtual > 5 ? 'completed' : 'pending',
    },
    {
      numero: 6,
      titulo: 'Resultados',
      descricao: 'Minuta editável, pareceres e prognóstico',
      icone: CheckCircle2,
      status: etapaAtual === 6 ? 'in-progress' : 'pending',
    },
  ];

  // ===== NAVEGAÇÃO =====

  /**
   * Avança para próxima etapa (com validação)
   */
  const avancarEtapa = () => {
    // Ajuste: agora temos 6 etapas (inclui etapa de Resultados)
    // Garantir que não ultrapassamos o número máximo de etapas
    if (etapaAtual < 6) {
      setEtapaAtual((prev) => (prev + 1) as EtapaWizard);
    }
  };

  // Sempre que a etapa atual mudar, persistir estado do wizard no backend
  useEffect(() => {
    (async () => {
      try {
        if (!processoId) return;

        const mapEtapa = (et: EtapaWizard) => {
          switch (et) {
            case 1: return 'upload';
            case 2: return 'documentos_complementares';
            case 3: return 'sindical_jurispr';
            case 4: return 'selecionar_agentes';
            case 5: return 'processing';
            case 6: return 'done';
            default: return 'upload';
          }
        };

        const estado = mapEtapa(etapaAtual);
        // Import dinamico para evitar ciclo de imports durante tests
        const api = await import('../servicos/servicoApiProcessos');
        await api.atualizarProcesso(processoId, { estado_wizard: estado, status_processamento: estado === 'processing' ? 'processing' : undefined });
        console.log('[AnalisePeticaoInicial] Estado de processo persistido:', processoId, estado);
      } catch (err) {
        console.warn('[AnalisePeticaoInicial] Falha ao persistir estado do processo (ignorado):', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etapaAtual]);

  // Quando documentos enviados mudam, persistir no metadados do processo
  useEffect(() => {
    (async () => {
      try {
        if (!processoId) return;
        if (!documentosEnviados || documentosEnviados.length === 0) return;
        // Avoid updating if identical to initial metadados
        try {
          const inicial = (metadadosIniciaisRef.current || {}) as MetadadosProcesso;
          if (JSON.stringify(inicial.documentos_enviados || []) === JSON.stringify(documentosEnviados)) {
            return;
          }
        } catch {
          // fallthrough
        }

        const api = await import('../servicos/servicoApiProcessos');
        await api.atualizarProcesso(processoId, { metadados: { documentos_enviados: documentosEnviados } });
        // update ref to avoid immediate re-writes
        if (!metadadosIniciaisRef.current) metadadosIniciaisRef.current = {};
        metadadosIniciaisRef.current.documentos_enviados = documentosEnviados;
        console.log('[AnalisePeticaoInicial] Documentos enviados persistidos no backend:', processoId, documentosEnviados);
      } catch (err) {
        console.warn('[AnalisePeticaoInicial] Falha ao persistir documentos_enviados (ignorado):', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentosEnviados]);

  // Quando agentes selecionados mudam, persistir no metadados do processo
  useEffect(() => {
    (async () => {
      try {
        if (!processoId) return;
        // Avoid updating if identical to initial metadados
        try {
          const inicial = (metadadosIniciaisRef.current || {}) as MetadadosProcesso;
          if (JSON.stringify(inicial.agentes_selecionados || {}) === JSON.stringify(agentesSelecionados)) {
            return;
          }
        } catch {
          // fallthrough
        }

        const api = await import('../servicos/servicoApiProcessos');
        await api.atualizarProcesso(processoId, { metadados: { agentes_selecionados: agentesSelecionados } });
        if (!metadadosIniciaisRef.current) metadadosIniciaisRef.current = {};
        metadadosIniciaisRef.current.agentes_selecionados = agentesSelecionados;
        console.log('[AnalisePeticaoInicial] Agentes selecionados persistidos no backend:', processoId, agentesSelecionados);
      } catch (err) {
        console.warn('[AnalisePeticaoInicial] Falha ao persistir agentes_selecionados (ignorado):', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentesSelecionados]);

  /**
   * Volta para etapa anterior
   */
  const voltarEtapa = () => {
    if (etapaAtual > 1) {
      setEtapaAtual((prev) => (prev - 1) as EtapaWizard);
    }
  };

  /**
   * Efeito para limpeza da conexão WebSocket ao desmontar o componente
   * 
   * CONTEXTO: Quando o usuário navega para fora da página de análise,
   * devemos fechar explicitamente a conexão WebSocket para notificar o backend
   * e parar tentativas de reconexão.
   */
  useEffect(() => {
    return () => {
      if (peticaoId) {
        console.log('[AnalisePeticaoInicial] 🔌 Cancelando análise e fechando WebSocket ao desmontar componente');
        // Cancel analysis in backend
        cancelarAnalise(peticaoId).catch(err => {
          console.warn('[AnalisePeticaoInicial] Falha ao cancelar análise:', err);
        });
        // Close WebSocket
        const servicoWebSocket = obterWebSocketService(token || undefined, uploadId || peticaoId);
        servicoWebSocket.closeManually();
      }
    };
  }, [peticaoId, uploadId, token]);

  // ===== RENDERIZAÇÃO =====

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-2 text-center">
        <h1 className="text-3xl font-bold text-gray-900">
          Análise Processual - Reclamada
        </h1>
        <p className="text-gray-600">
          Fluxo guiado para análise completa com prognóstico e pareceres especializados
        </p>
      </div>

      {/* Stepper (indicador de progresso) */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          {etapas.map((etapa, index) => (
            <div key={etapa.numero} className="flex flex-1 items-center">
              {/* Círculo da etapa */}
              <div className="flex flex-col items-center">
                <div
                  className={`
                    flex size-12 items-center justify-center rounded-full border-2
                    ${etapa.status === 'completed'
                      ? 'border-green-500 bg-green-500 text-white'
                      : etapa.status === 'in-progress'
                        ? 'border-primary-500 bg-primary-500 text-white'
                        : etapa.status === 'error'
                          ? 'border-red-500 bg-red-500 text-white'
                          : 'border-gray-300 bg-white text-gray-400'
                    }
                  `}
                >
                  {etapa.status === 'completed' ? (
                    <CheckCircle2 className="size-6" />
                  ) : (
                    <etapa.icone className="size-6" />
                  )}
                </div>
                <div className="mt-2 hidden min-h-[48px] text-center md:block">
                  <div
                    role="button"
                    tabIndex={0}
                    title={etapa.descricao}
                    aria-label={etapa.descricao}
                    className={`mx-auto max-w-[200px] whitespace-normal break-words text-sm font-medium ${etapa.status === 'in-progress' ? 'text-primary-600' : 'text-gray-600'
                      }`}
                  >
                    {etapa.titulo}
                  </div>
                </div>
              </div>

              {/* Linha conectora (exceto no último) */}
              {index < etapas.length - 1 && (
                <div
                  className={`
                    mx-4 h-0.5 flex-1
                    ${etapa.status === 'completed' ? 'bg-green-500' : 'bg-gray-300'}
                  `}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Mensagem de erro (se houver) */}
      {erro && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 text-red-600">
              <svg className="size-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Erro</h3>
              <p className="mt-1 text-sm text-red-700">{erro}</p>
            </div>
          </div>
        </div>
      )}

      {/* Conteúdo da etapa atual */}
      <div className="card min-h-[400px]">
        {etapaAtual === 1 && (
          <EtapaUploadPeticao
            processoIdAtivo={processoId}
            onUploadConcluido={(peticaoId, uploadId, tipo, pid) => {
              setPeticaoId(peticaoId);
              setUploadId(uploadId);
              // variáveis removidas por não uso: tipo
              if (pid) {
                setProcessoId(pid);
              }
              avancarEtapa();
            }}
            onDocumentosSugeridos={setDocumentosSugeridos}
            onArquivosIgnorados={setArquivosIgnorados}
            onErro={setErro}
          />
        )}

        {etapaAtual === 2 && (
          <EtapaDocumentosComplementares
            peticaoId={peticaoId}
            documentosSugeridos={documentosSugeridos}
            arquivosIgnorados={arquivosIgnorados}
            onDocumentosEnviados={setDocumentosEnviados}
            onAvancar={avancarEtapa}
            onVoltar={voltarEtapa}
            onErro={setErro}
          />
        )}

        {etapaAtual === 3 && (
          <div className="space-y-6">
            {/* Top row: Back button (left) - keeps the affordance consistent across steps */}
            <div className="flex items-center justify-start">
              <button
                onClick={voltarEtapa}
                className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-200"
              >
                ← Voltar
              </button>
            </div>

            {/* Centered container for the upload component to improve presentation */}
            <div className="flex justify-center">
              <div className="w-full max-w-3xl">
                <ComponenteUploadSindicaisJurisprudencia
                  processoId={processoId || undefined}
                  aoFinalizarUploads={(idsPorCategoria) => {
                    // Adiciona os ids recebidos ao array documentosEnviados
                    const novosIds: string[] = [];
                    if (idsPorCategoria.convencoes_sindicais) {
                      novosIds.push(...idsPorCategoria.convencoes_sindicais);
                    }
                    if (idsPorCategoria.jurisprudencia) {
                      novosIds.push(...idsPorCategoria.jurisprudencia);
                    }
                    if (novosIds.length > 0) {
                      setDocumentosEnviados((prev) => [...prev, ...novosIds]);
                    }
                  }}
                  aoOcorrerErro={(mensagemErro) => setErro(mensagemErro)}
                />
              </div>
            </div>

            {/* Footer actions: align navigation buttons to the right for consistency */}
            <div className="mt-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={voltarEtapa}
                className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-700 hover:bg-gray-200"
              >
                ← Voltar
              </button>

              <button
                onClick={() => {
                  // Ao prosseguir, avançar para seleção de agentes
                  avancarEtapa();
                }}
                className="rounded-md bg-primary-600 px-4 py-2 text-white hover:bg-primary-700"
              >
                ✅ Prosseguir
              </button>
            </div>
          </div>
        )}

        {etapaAtual === 4 && (
          <EtapaSelecaoAgentes
            peticaoId={peticaoId}
            documentosEnviados={documentosEnviados}
            agentesSelecionados={agentesSelecionados}
            onAgentesAlterados={setAgentesSelecionados}
            onAvancar={avancarEtapa}
            onVoltar={voltarEtapa}
          />
        )}

        {etapaAtual === 5 && (
          <EtapaProcessamento
            key={`processamento-${peticaoId}`}
            peticaoId={peticaoId}
            uploadId={uploadId}
            agentesSelecionados={agentesSelecionados}
            progresso={progressoAnalise}
            etapa={etapaAnalise}
            token={token}
            onProgressoAtualizado={(prog, etapa) => {
              setProgressoAnalise(prog);
              setEtapaAnalise(etapa);
            }}
            onAnaliseConcluida={(resultado) => {
              setResultado(resultado);
              avancarEtapa();
            }}
            onErro={setErro}
            retomarSomentePolling={retomarSomentePolling}
          />
        )}

        {etapaAtual === 6 && resultado && (
          <EtapaResultados
            resultado={resultado}
            processoId={processoId}
            onNovaAnalise={() => {
              // Reset state e volta para etapa 1
              setEtapaAtual(1);
              setPeticaoId('');
              setDocumentosSugeridos([]);
              setDocumentosEnviados([]);
              setAgentesSelecionados({ advogados: [], peritos: [] });
              setProgressoAnalise(0);
              setEtapaAnalise('');
              setResultado(null);
              setErro('');
            }}
          />
        )}
      </div>
    </div>
  );
}

// ===== COMPONENTES DE ETAPA (PLACEHOLDERS) =====

/**
 * ETAPA 1: Upload da Análise Processual (documento)
 * 
 * IMPLEMENTAÇÃO (TAREFA-050): Usa ComponenteUploadPeticaoInicial
 */
function EtapaUploadPeticao({
  processoIdAtivo,
  onUploadConcluido,
  onDocumentosSugeridos,
  onArquivosIgnorados,
  onErro,
}: {
  processoIdAtivo?: string;
  onUploadConcluido: (peticaoId: string, uploadId: string, tipoAcao: string, processoId?: string) => void;
  onDocumentosSugeridos: (docs: DocumentoSugerido[]) => void;
  onArquivosIgnorados: (arquivos: ArquivoIgnorado[]) => void;
  onErro: (erro: string) => void;
}) {
  // Preview removido: avançamos automaticamente após o upload

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="mb-2 text-2xl font-bold text-gray-900">
          Upload da Análise Processual
        </h2>
        <p className="text-gray-600">
          Escolha uma das opções abaixo para enviar seus documentos. Você pode enviar apenas a petição inicial ou uma pasta compactada contendo todos os documentos do caso.
        </p>
        <div className="mt-4 text-sm text-gray-500">
          <p className="mb-2">
            <strong>Anexar Petição:</strong> Envie apenas o documento da petição inicial (PDF ou DOCX) para uma análise rápida.
          </p>
          <p>
            <strong>Anexar Pasta (.zip):</strong> Envie uma pasta compactada contendo todos os documentos do caso para uma análise mais completa.
          </p>
        </div>
      </div>

      {/* Componente de upload: ao concluir, avançamos automaticamente */}
      <ComponenteUploadPeticaoInicial
        aoConcluirComSucesso={async (peticaoId, documentosSugeridos, arquivosIgnorados) => {
          console.log('[EtapaUploadPeticao] Upload concluído. Avançando automaticamente:', peticaoId);
          console.log('[EtapaUploadPeticao] Arquivos ignorados:', arquivosIgnorados);
          
          // Repassar arquivos ignorados para o componente pai
          if (arquivosIgnorados && arquivosIgnorados.length > 0) {
            onArquivosIgnorados(arquivosIgnorados);
          }
          
          try {
            // Prefer processoIdAtivo, senão tenta encontrar um existente para este documento/processo, senão cria novo
            let pid = processoIdAtivo || '';

            if (!pid && peticaoId) {
              try {
                console.log(`[EtapaUploadPeticao] Verificando processo existente para o documento ${peticaoId}...`);
                const apiProc = await import('../servicos/servicoApiProcessos');
                const res = await apiProc.listarProcessos();
                type ProcList = { processos?: { processo_id?: string; id?: string; peticoes?: string[]; metadados?: { peticao_id?: string } }[] };
                const processos = ((res.data as ProcList)?.processos) ?? [];

                const processoExistente = processos.find(p =>
                  (p.peticoes && p.peticoes.includes(peticaoId)) ||
                  (p.metadados && p.metadados.peticao_id === peticaoId)
                );

                if (processoExistente) {
                  pid = processoExistente.processo_id || processoExistente.id || pid;
                  console.log(`[EtapaUploadPeticao] Processo existente encontrado: ${pid}`);
                }
              } catch (errFind) {
                console.warn('[EtapaUploadPeticao] Erro ao tentar encontrar processo existente (ignorado):', errFind);
              }
            }

            if (!pid) {
              console.log('[EtapaUploadPeticao] Criando novo processo no backend...');
              const resp = await criarProcesso({
                nome: `Análise - ${new Date().toLocaleString()}`,
                descricao: 'Processo criado automaticamente pelo frontend ao enviar o documento (processo)',
                tipo_vertical: 'analise_processual', // TAREFA-224: Definir tipo de vertical
              });
              pid = resp.data.processo_id;
              console.log('[EtapaUploadPeticao] Processo criado:', pid);
            }

            // Persistir metadados iniciais do processo
            try {
              const metadados = {
                peticao_id: peticaoId,
                documentos_sugeridos: documentosSugeridos || [],
                estado_wizard: 'upload'
              };

              const api = await import('../servicos/servicoApiProcessos');
              try {
                await api.atualizarProcesso(pid, { metadados, estado_wizard: 'upload' });
                console.log('[EtapaUploadPeticao] Metadados persistidos no backend para processo', pid);
              } catch (errPersist) {
                console.warn('[EtapaUploadPeticao] Falha ao atualizar processo (tentando recriar):', errPersist);
                try {
                  const respNovo = await criarProcesso({
                    nome: `Análise - ${new Date().toLocaleString()}`,
                    descricao: 'Reconstruído após falha ao atualizar processo anterior',
                    tipo_vertical: 'analise_processual', // TAREFA-224: Definir tipo de vertical
                  });
                  pid = respNovo.data.processo_id;
                  await api.atualizarProcesso(pid, { metadados, estado_wizard: 'upload' });
                  console.log('[EtapaUploadPeticao] Metadados persistidos no backend após recriar processo', pid);
                } catch (errRecreate) {
                  console.warn('[EtapaUploadPeticao] Não foi possível recriar/persistir metadados (ignorado):', errRecreate);
                }
              }
            } catch (errPersistGeral) {
              console.warn('[EtapaUploadPeticao] Não foi possível persistir metadados no backend (ignorado):', errPersistGeral);
            }

            // Avança: repassa documentos e informa upload concluído
            onDocumentosSugeridos(documentosSugeridos || []);
            onUploadConcluido(peticaoId, '', '', pid);
          } catch (err) {
            console.error('[EtapaUploadPeticao] Erro ao criar/validar processo no backend:', err);
            // Mesmo que a criação do processo falhe, permitimos continuar localmente
            onDocumentosSugeridos(documentosSugeridos || []);
            onUploadConcluido(peticaoId, '', '', '');
          }
        }}
        aoOcorrerErro={(mensagemErro) => {
          console.error('[EtapaUploadPeticao] Erro:', mensagemErro);
          onErro(mensagemErro);
        }}
      />
      {/* Pré-visualização removida */}
    </div>
  );
}

/**
 * ETAPA 2: Documentos Complementares
 * 
 * IMPLEMENTAÇÃO (TAREFA-051): Usa ComponenteDocumentosSugeridos
 * 
 * CORREÇÃO CRÍTICA: Agora busca documentos já enviados ao montar para
 * preservar o estado quando o usuário sai e retorna à página.
 */
function EtapaDocumentosComplementares({
  peticaoId,
  documentosSugeridos,
  arquivosIgnorados = [],
  onDocumentosEnviados,
  onAvancar,
  onVoltar,
  onErro,
}: {
  peticaoId: string;
  documentosSugeridos: DocumentoSugerido[];
  arquivosIgnorados?: ArquivoIgnorado[];
  onDocumentosEnviados: (ids: string[]) => void;
  onAvancar: () => void;
  onVoltar: () => void;
  onErro: (erro: string) => void;
}) {
  // Estado para armazenar documentos já enviados (carregados do backend)
  const [documentosJaEnviados, setDocumentosJaEnviados] = useState<Array<{
    documento_id: string;
    nome_arquivo: string;
    tipo_documento?: string;
    status_upload: string;
    progresso_percentual?: number;
  }>>([]);
  
  // Estado para indicar se está carregando documentos anteriores
  const [carregandoDocumentos, setCarregandoDocumentos] = useState<boolean>(true);

  /**
   * CORREÇÃO CRÍTICA: Ao montar, buscar documentos já enviados do backend
   * para restaurar o estado caso o usuário tenha saído e retornado à página.
   */
  useEffect(() => {
    let isMounted = true;
    
    async function carregarDocumentosEnviados() {
      if (!peticaoId) {
        setCarregandoDocumentos(false);
        return;
      }
      
      try {
        console.log('[EtapaDocumentosComplementares] Buscando documentos já enviados para peticao:', peticaoId);
        const resposta = await listarDocumentosPeticao(peticaoId);
        
        if (isMounted && resposta.data.documentos_enviados) {
          const docs = resposta.data.documentos_enviados.map((doc: {
            documento_id: string;
            nome_arquivo: string;
            tipo_documento?: string;
            status_upload: string;
            progresso_percentual?: number;
          }) => ({
            documento_id: doc.documento_id,
            nome_arquivo: doc.nome_arquivo,
            tipo_documento: doc.tipo_documento,
            status_upload: doc.status_upload,
            progresso_percentual: doc.progresso_percentual
          }));
          
          setDocumentosJaEnviados(docs);
          console.log('[EtapaDocumentosComplementares] Documentos já enviados carregados:', docs.length, 'docs:', docs.map(d => ({ nome: d.nome_arquivo, tipo: d.tipo_documento, status: d.status_upload })));
          
          // Se já tem documentos enviados, atualizar o estado pai também
          if (docs.length > 0) {
            const ids = docs.map((d: { documento_id: string }) => d.documento_id);
            onDocumentosEnviados(ids);
          }
        }
      } catch (erro) {
        console.warn('[EtapaDocumentosComplementares] Erro ao carregar documentos (pode ser primeira vez):', erro);
      } finally {
        if (isMounted) {
          setCarregandoDocumentos(false);
        }
      }
    }
    
    carregarDocumentosEnviados();
    
    return () => {
      isMounted = false;
    };
  }, [peticaoId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mostrar loading enquanto carrega documentos anteriores
  if (carregandoDocumentos) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="mb-4 size-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600"></div>
          <p className="text-sm text-gray-600">Carregando documentos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Botão Voltar */}
      <div className="flex justify-start">
        <button
          onClick={onVoltar}
          className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-200"
        >
          ← Voltar
        </button>
      </div>

      {/* Componente de Documentos Sugeridos. Ao completar, avançamos automaticamente para a próxima etapa. */}
      <ComponenteDocumentosSugeridos
        peticaoId={peticaoId}
        documentosSugeridos={documentosSugeridos}
        documentosJaEnviados={documentosJaEnviados}
        arquivosIgnorados={arquivosIgnorados}
        aoCompletarDocumentos={async (documentosIds) => {
          console.log('[EtapaDocumentosComplementares] Documentos enviados. Buscando lista completa da API.', documentosIds);
          // CORREÇÃO: Em vez de apenas repassar os novos IDs (que substitui o array),
          // buscamos a lista completa de documentos da API para garantir que temos
          // tanto os documentos do ZIP quanto os complementares
          try {
            const resposta = await listarDocumentosPeticao(peticaoId);
            const todosDocumentosIds = resposta.data.documentos_enviados?.map(
              (doc: { documento_id: string }) => doc.documento_id
            ) || [];
            console.log('[EtapaDocumentosComplementares] Lista completa de documentos da API:', todosDocumentosIds);
            onDocumentosEnviados(todosDocumentosIds);
          } catch (erro) {
            console.error('[EtapaDocumentosComplementares] Erro ao buscar lista completa de documentos:', erro);
            // Fallback: usar apenas os IDs retornados
            onDocumentosEnviados(documentosIds);
          }
          onAvancar();
        }}
        aoOcorrerErro={(mensagemErro) => {
          console.error('[EtapaDocumentosComplementares] Erro:', mensagemErro);
          onErro(mensagemErro);
        }}
      />
    </div>
  );
}

/**
 * ETAPA 3: Seleção de Agentes
 * 
 * IMPLEMENTAÇÃO (TAREFA-052): Usa ComponenteSelecaoAgentesPeticao
 */
function EtapaSelecaoAgentes({
  peticaoId,
  documentosEnviados,
  agentesSelecionados,
  onAgentesAlterados,
  onAvancar,
  onVoltar,
}: {
  peticaoId?: string;
  documentosEnviados?: string[];
  agentesSelecionados: AgentesSelecionados;
  onAgentesAlterados: (agentes: AgentesSelecionados) => void;
  onAvancar: () => void;
  onVoltar: () => void;
}) {
  // Estado local para controle do resumo final antes de iniciar a análise
  const [mostrarResumo, setMostrarResumo] = useState<boolean>(false);
  const [agentesLocais, setAgentesLocais] = useState<AgentesSelecionados>(agentesSelecionados);

  return (
    <div className="space-y-6">
      {!mostrarResumo ? (
        <ComponenteSelecaoAgentesPeticao
          agentesSelecionados={agentesLocais}
          onAgentesAlterados={(novos) => {
            setAgentesLocais(novos);
            onAgentesAlterados(novos);
          }}
          onAvancar={() => {
            // Em vez de avançar imediatamente, mostramos o resumo para confirmação (TAREFA-061)
            setMostrarResumo(true);
          }}
          onVoltar={onVoltar}
        />
      ) : (
        <ResumoPreAnalise
          peticaoId={peticaoId || ''}
          documentosEnviados={documentosEnviados || []}
          documentosSugeridosCount={0}
          agentesSelecionados={agentesLocais}
          onEditar={() => setMostrarResumo(false)}
          onConfirmar={() => {
            // Usuário confirmou resumo -> avançar para processamento
            onAvancar();
          }}
        />
      )}
    </div>
  );
}

/**
 * ETAPA 5: Processamento
 * 
 * CONTEXTO DE NEGÓCIO:
 * Exibe feedback visual do progresso da análise multi-agent enquanto o backend
 * processa o processo/documento. Esta etapa foi refatorada na TAREFA-095 para usar WebSocket
 * ao invés de polling, proporcionando atualizações em tempo real mais eficientes.
 * 
 * IMPLEMENTAÇÃO:
 * - Inicia análise via POST /api/peticoes/{id}/analisar
 * - Conecta ao WebSocket em /ws/analise/status para receber atualizações
 * - Atualiza barra de progresso em tempo real baseado em mensagens WebSocket
 * - Busca resultado quando status = "CONCLUIDA" via WebSocket
 * - Trata erros comunicados via WebSocket
 * 
 * MUDANÇAS (TAREFA-095):
 * - ❌ REMOVIDO: Polling via setInterval
 * - ✅ ADICIONADO: Integração com websocketService.ts
 * - ✅ ADICIONADO: Subscribe/unsubscribe em lifecycle do componente
 * - ✅ ADICIONADO: Filtro de mensagens por consulta_id (peticaoId)
 */
function EtapaProcessamento({
  peticaoId,
  uploadId,
  processoId,
  agentesSelecionados,
  progresso,
  etapa,
  onProgressoAtualizado,
  onAnaliseConcluida,
  onErro,
  retomarSomentePolling = false,
  token,
}: {
  peticaoId: string;
  uploadId?: string;
  processoId?: string;
  agentesSelecionados: AgentesSelecionados;
  progresso: number;
  etapa: string;
  onProgressoAtualizado: (progresso: number, etapa: string) => void;
  onAnaliseConcluida: (resultado: ResultadoAnaliseProcesso) => void;
  onErro: (erro: string) => void;
  token: string | null;
  /**
   * Se true, não dispara o POST /api/peticoes/{id}/analisar — apenas reconecta
   * ao WebSocket para receber atualizações (útil para re-hidratação quando o
   * backend já iniciou o processamento).
   */
  retomarSomentePolling?: boolean;
}) {
  // Flag persistente para prevenir dupla execução (React 18 StrictMode)
  const analiseIniciadaRef = useRef(false);

  // Timeout para reconexão após refresh
  const timeoutReconexaoRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Log de montagem do componente
  // Inclui 'peticaoId' e 'agentesSelecionados' nas deps para satisfazer o linter;
  // o efeito apenas registra montagem/desmontagem e pode executar quando essas props mudarem.
  useEffect(() => {
    console.log('[EtapaProcessamento] 🔵 COMPONENTE MONTADO', {
      peticaoId,
      agentesSelecionados,
      analiseJaIniciada: analiseIniciadaRef.current
    });

    return () => {
      console.log('[EtapaProcessamento] 🔴 COMPONENTE DESMONTADO', {
        peticaoId,
        analiseJaIniciada: analiseIniciadaRef.current
      });
    };
  }, [peticaoId, agentesSelecionados]);

  /**
   * Efeito principal que gerencia o ciclo de vida da análise com WebSocket
   * 
   * CONTEXTO:
   * - TAREFA-095: Substituição de polling por WebSocket para atualizações em tempo real
   * - Conecta ao WebSocket ao montar o componente
   * - Subscreve para receber atualizações de status da análise
   * - Desconecta e limpa ao desmontar
   * 
   * IMPLEMENTAÇÃO:
   * 1. Inicia análise (se necessário) via POST /api/peticoes/{id}/analisar
   * 2. Conecta ao WebSocket para receber atualizações
   * 3. Atualiza progresso em tempo real baseado nas mensagens recebidas
   * 4. Busca resultado quando status = "CONCLUIDA"
   * 5. Trata erros via WebSocket
   */
  useEffect(() => {
    let isMounted = true;
    let unsubscribeWebSocket: (() => void) | null = null;
    let pararPolling: (() => void) | null = null; // Referência para parar polling de fallback
    const currentToken = token; // Usar token passado como prop

    /**
     * Função de fallback: ativa polling quando WebSocket falha
     * CONTEXTO (CORREÇÃO TAREFA-WS-AUTH):
     * Se o WebSocket falhar após múltiplas tentativas de reconexão,
     * ativamos polling como mecanismo de recuperação.
     */
    const ativarFallbackPolling = () => {
      console.log('[EtapaProcessamento] 📊 Ativando fallback de polling...');
      
      const { parar } = iniciarPollingStatus(
        uploadId || peticaoId,
        // onUpdate
        (status) => {
          if (!isMounted) return;
          onProgressoAtualizado(
            status.progresso_percentual || 0,
            String(status.etapa_atual || 'Processando...')
          );
        },
        // onComplete
        async (status) => {
          if (!isMounted) return;
          console.log('[EtapaProcessamento] ✅ Análise concluída via polling fallback');
          try {
            const respostaResultado = await obterResultadoAnalise(peticaoId);
            onAnaliseConcluida(respostaResultado.data);
          } catch (err) {
            onErro(err instanceof Error ? err.message : 'Erro ao buscar resultado');
          }
          sessionStorage.removeItem('upload_id');
        },
        // onError
        (err) => {
          if (!isMounted) return;
          console.error('[EtapaProcessamento] ❌ Erro no polling fallback:', err);
          onErro(err instanceof Error ? err.message : 'Erro no acompanhamento da análise');
        },
        2000 // Polling a cada 2 segundos
      );
      
      pararPolling = parar;
    };

    /**
     * Função para renovar token JWT
     * CONTEXTO (CORREÇÃO TAREFA-WS-AUTH):
     * Quando WebSocket falha com erro 403/4403 (token expirado),
     * tentamos renovar usando o refresh_token.
     */
    const tentarRenovarToken = async (): Promise<string | null> => {
      try {
        const refreshTokenSalvo = localStorage.getItem(LOCALSTORAGE_REFRESH_TOKEN_KEY);
        if (!refreshTokenSalvo) {
          console.warn('[EtapaProcessamento] Sem refresh token disponível para renovação');
          return null;
        }
        
        console.log('[EtapaProcessamento] 🔄 Tentando renovar token JWT...');
        const resposta = await renovarToken(refreshTokenSalvo);
        
        // Atualizar token no localStorage
        localStorage.setItem(LOCALSTORAGE_TOKEN_KEY, resposta.access_token);
        
        console.log('[EtapaProcessamento] ✅ Token renovado com sucesso');
        return resposta.access_token;
      } catch (err) {
        console.error('[EtapaProcessamento] ❌ Falha ao renovar token:', err);
        return null;
      }
    };

    const iniciarAnaliseComWebSocket = async () => {
      console.log('[EtapaProcessamento] 🚀 Iniciando análise com WebSocket...', {
        analiseJaIniciada: analiseIniciadaRef.current,
        peticaoId,
      });

      // Obter instância singleton do serviço WebSocket
      const servicoWebSocket = obterWebSocketService(currentToken || undefined, uploadId || peticaoId);
      
      // Configurar callback de renovação de token (CORREÇÃO TAREFA-WS-AUTH)
      servicoWebSocket.setCallbackRenovarToken(tentarRenovarToken);
      
      // Configurar callback de fallback para polling (CORREÇÃO TAREFA-WS-AUTH)
      servicoWebSocket.setCallbackFallbackPolling(ativarFallbackPolling);

      try {
        // CORREÇÃO CRÍTICA: SEMPRE inscrever no WebSocket, mesmo em remontagem
        // O problema era que após desmontagem (StrictMode), a re-montagem não se inscrevia novamente
        // deixando o componente sem receber atualizações
        unsubscribeWebSocket = servicoWebSocket.subscribe((atualizacao: AtualizacaoStatusAnalise) => {
          // Filtrar apenas atualizações do processo/documento atual
          console.log('[EtapaProcessamento] 🔍 Comparando IDs:', {
            'consulta_id (recebido)': atualizacao.consulta_id,
            'peticaoId (esperado)': peticaoId,
            'uploadId (esperado)': uploadId,
            'match': atualizacao.consulta_id === (uploadId || peticaoId)
          });

          if (atualizacao.consulta_id !== (uploadId || peticaoId)) {
            console.log('[EtapaProcessamento] ⚠️ Ignorando atualização de outra análise:', atualizacao.consulta_id);
            return;
          }

          console.log('[EtapaProcessamento] 📨 ✅ Atualização WebSocket aceita:', {
            status: atualizacao.status,
            progresso: atualizacao.progresso_percentual,
            etapa: atualizacao.etapa_atual,
            consulta_id: atualizacao.consulta_id
          });

          if (!isMounted) return;

          // Atualizar progresso baseado no status
          if (atualizacao.status === 'PROCESSANDO' || atualizacao.status === 'INICIADA') {
            console.log('[EtapaProcessamento] 🔄 Status PROCESSANDO/INICIADA - atualizando progresso:', atualizacao.progresso_percentual);
            onProgressoAtualizado(
              atualizacao.progresso_percentual || 0,
              String(atualizacao.etapa_atual || 'Processando...')
            );
          } else if (atualizacao.status === 'CONCLUIDA') {
            console.log('[EtapaProcessamento] ✅ Status CONCLUIDA recebido! Iniciando busca de resultado...');

            // Clear reconnection timeout
            if (timeoutReconexaoRef.current) {
              clearTimeout(timeoutReconexaoRef.current);
              timeoutReconexaoRef.current = null;
            }
            // Análise concluída - buscar resultado
            console.log('[EtapaProcessamento] ✅ Análise concluída! Buscando resultado...');

            (async () => {
              try {
                if (!isMounted) return;

                // Tentar obter resultado do processo (cache)
                if (processoId) {
                  try {
                    const api = await import('../servicos/servicoApiProcessos');
                    const respProc = await api.obterProcesso(processoId);
                    const procData = respProc.data;
                    const resultadoPersistido = (procData?.metadados?.resultado_analise) || null;
                    if (resultadoPersistido) {
                      console.log('[EtapaProcessamento] Resultado encontrado em metadados do processo');
                      onAnaliseConcluida(resultadoPersistido as ResultadoAnaliseProcesso);
                      return;
                    }
                  } catch (errProc) {
                    console.warn('[EtapaProcessamento] Falha ao obter processo para resultado persistido:', errProc);
                  }
                }

                // Buscar resultado via API de processo/petição
                const respostaResultado = await obterResultadoAnalise(peticaoId);
                if (!isMounted) return;

                console.log('[EtapaProcessamento] Resultado obtido via API de processo/petição:', respostaResultado.data);

                // Persistir resultado no processo para cache futuro
                if (processoId) {
                  try {
                    const api = await import('../servicos/servicoApiProcessos');
                    await api.atualizarProcesso(processoId, {
                      metadados: { resultado_analise: respostaResultado.data },
                      estado_wizard: 'concluida',
                      status_processamento: 'done'
                    });
                    console.log('[EtapaProcessamento] Resultado também persistido no processo', processoId);
                  } catch (errPersist) {
                    console.warn('[EtapaProcessamento] Falha ao persistir resultado no processo:', errPersist);
                  }
                }

                onAnaliseConcluida(respostaResultado.data);
              } catch (erroResultado) {
                if (isMounted) {
                  console.error('[EtapaProcessamento] Erro ao buscar resultado:', erroResultado);
                  onErro(erroResultado instanceof Error ? erroResultado.message : 'Erro ao buscar resultado da análise');
                }
              }
            })();

            // Clear sessionStorage since analysis completed
            sessionStorage.removeItem('upload_id');
          } else if (atualizacao.status === 'ERRO') {
            // Erro na análise
            const mensagemErro = atualizacao.mensagem_erro || 'Erro desconhecido na análise';
            console.error('[EtapaProcessamento] ❌ Erro na análise:', mensagemErro);
            onErro(mensagemErro);

            // Clear sessionStorage on error
            sessionStorage.removeItem('upload_id');
          }
        });

        console.log('[EtapaProcessamento] 🔌 Inscrito no WebSocket para receber atualizações');

        // Conectar ao WebSocket (se ainda não estiver conectado)
        servicoWebSocket.connect();

        // Verificar se já iniciamos a análise para este processo (prevenir duplicação)
        // NOTA: Esta verificação está APÓS a inscrição do WebSocket para garantir que
        // o componente sempre recebe atualizações, mesmo em remontagens (StrictMode)
        const jaIniciouAnalise = !retomarSomentePolling && analiseIniciadaRef.current;

        if (jaIniciouAnalise) {
          console.log('[EtapaProcessamento] ✨ Análise já iniciada anteriormente, apenas reconectando ao WebSocket');
          analiseIniciadaRef.current = true; // Garantir que está marcado
          return; // Não chamar API novamente
        }

        // Marcar que iniciamos a análise (antes de chamar API para evitar race condition)
        analiseIniciadaRef.current = true;

        // Iniciar análise no backend (se não for re-hidratação)
        if (retomarSomentePolling) {
          console.log('[EtapaProcessamento] ✨ Re-hidratação: aguardando atualizações via WebSocket para', peticaoId);

          // Set timeout for reconnection failure
          timeoutReconexaoRef.current = setTimeout(() => {
            console.warn('[EtapaProcessamento] Timeout de reconexão atingido, assumindo falha');
            onErro('Processamento interrompido. O processo pode ter sido concluído ou cancelado. Tente iniciar uma nova análise.');
            sessionStorage.removeItem('upload_id');
          }, 30000); // 30 seconds
        } else {
          console.log('[EtapaProcessamento] 📤 Iniciando análise via API...', {
            peticaoId,
            agentesSelecionados,
            timestamp: new Date().toISOString(),
          });

          await iniciarAnalise(peticaoId, {
            agentes_selecionados: agentesSelecionados,
          });

          // Store upload_id in sessionStorage for page refresh reconnection
          sessionStorage.setItem('upload_id', uploadId || peticaoId);

          if (!isMounted) {
            console.log('[EtapaProcessamento] 🛑 Componente desmontado antes de concluir inicialização. Abortando.');
            return;
          }

          console.log('[EtapaProcessamento] ✅ Análise iniciada. Aguardando atualizações via WebSocket...');
        }
      } catch (erroInicio) {
        if (isMounted) {
          console.error('[EtapaProcessamento] Erro ao iniciar análise ou conectar WebSocket:', erroInicio);
          onErro(
            erroInicio instanceof Error
              ? erroInicio.message
              : 'Erro ao iniciar análise'
          );
        }
      }
    };

    iniciarAnaliseComWebSocket();

    // Cleanup: desinscrever do WebSocket ao desmontar
    return () => {
      isMounted = false;
      if (unsubscribeWebSocket) {
        console.log('[EtapaProcessamento] 🔌 Desinscrevendo do WebSocket ao desmontar.');
        unsubscribeWebSocket();
      }
      // Parar polling de fallback se ativo
      if (pararPolling) {
        console.log('[EtapaProcessamento] 📊 Parando polling de fallback ao desmontar.');
        pararPolling();
      }
      // Clear reconnection timeout
      if (timeoutReconexaoRef.current) {
        clearTimeout(timeoutReconexaoRef.current);
        timeoutReconexaoRef.current = null;
      }
      // IMPORTANTE: Liberar referência ao serviço WebSocket singleton
      // (Não chamar disconnect() pois outros componentes podem estar usando)
      const servicoWebSocket = obterWebSocketService(currentToken || undefined, uploadId || peticaoId);
      servicoWebSocket.release();
      console.log('[EtapaProcessamento] 🔌 Referência ao WebSocket liberada.');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peticaoId, processoId, retomarSomentePolling]); // restart effect when key inputs change

  // ===== RENDERIZAÇÃO =====

  return (
    <div className="mx-auto max-w-4xl px-4 py-12">
      {/* Card principal com sombra e fundo gradiente sutil */}
      <div className="rounded-2xl border border-primary-100 bg-gradient-to-br from-white to-primary-50 p-8 shadow-xl md:p-12">
        {/* Ícone animado com pulso */}
        <div className="mb-8 flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 animate-ping rounded-full bg-primary-200 opacity-25"></div>
            <BarChart3 className="relative z-10 size-20 animate-pulse text-primary-600" />
          </div>
        </div>

        {/* Título e descrição */}
        <div className="mb-10 text-center">
          <h2 className="mb-3 text-3xl font-bold text-gray-900">
            Análise Multi-Agent em Andamento
          </h2>
          <p className="mb-2 text-lg text-gray-700">
            Nossos agentes especialistas estão analisando seu processo...
          </p>
          <p className="text-sm text-gray-600">
            Este processo envolve múltiplos agentes de IA trabalhando em paralelo
          </p>
        </div>

        {/* Barra de progresso aprimorada */}
        <div className="mb-8">
          {/* Percentual grande e destacado */}
          <div className="mb-4 text-center">
            <span className="text-5xl font-bold tabular-nums text-primary-600">
              {progresso}%
            </span>
          </div>

          {/* Barra de progresso com gradiente e animação */}
          <div className="relative h-6 overflow-hidden rounded-full bg-gray-200 shadow-inner">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 shadow-lg transition-all duration-700 ease-out"
              style={{ width: `${progresso}%` }}
            >
              {/* Brilho animado na barra */}
              <div className="animate-shimmer absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"></div>
            </div>
          </div>
        </div>

        {/* Etapa atual com ícone e texto descritivo */}
        <div className="mb-8 rounded-xl border border-gray-100 bg-white p-6 shadow-md">
          <div className="flex items-start gap-4">
            <div className="shrink-0">
              <div className="flex size-12 items-center justify-center rounded-full bg-primary-100">
                <div className="size-3 animate-pulse rounded-full bg-primary-600"></div>
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-gray-500">
                Etapa Atual
              </h3>
              <p className="text-lg font-medium text-gray-900">
                {etapa || 'Iniciando análise...'}
              </p>
            </div>
          </div>
        </div>

        {/* Lista de agentes selecionados (se disponível) */}
        {(agentesSelecionados.advogados.length > 0 || agentesSelecionados.peritos.length > 0) && (
          <div className="mb-8 rounded-xl border border-primary-100 bg-primary-50 p-6">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-700">
              Agentes Trabalhando
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {agentesSelecionados.advogados.length > 0 && (
                <div>
                  <h4 className="mb-2 text-xs font-medium text-gray-600">Advogados</h4>
                  <ul className="space-y-1">
                    {agentesSelecionados.advogados.map((adv, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="size-1.5 rounded-full bg-primary-500"></span>
                        {adv}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {agentesSelecionados.peritos.length > 0 && (
                <div>
                  <h4 className="mb-2 text-xs font-medium text-gray-600">Peritos</h4>
                  <ul className="space-y-1">
                    {agentesSelecionados.peritos.map((per, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                        <span className="size-1.5 rounded-full bg-primary-500"></span>
                        {per}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Aviso importante destacado */}
        <div className="rounded-r-lg border-l-4 border-amber-400 bg-amber-50 p-4">
          <div className="flex items-center gap-3">
            <svg className="size-6 shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-amber-800">
                Não feche esta página
              </p>
              <p className="mt-1 text-xs text-amber-700">
                O processamento pode levar alguns minutos. Seu progresso está sendo salvo automaticamente.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * ETAPA 6: Resultados
 * 
 * CONTEXTO DE NEGÓCIO:
 * Esta etapa apresenta os resultados finais da análise multi-agent de forma clara,
 * organizada e visualmente atraente. O design prioriza a hierarquia de informações
 * e facilita a navegação entre as diferentes seções de resultados.
 * 
 * FUNCIONALIDADES:
 * - Exibição de header de conclusão com ícone de sucesso
 * - Apresentação estruturada em acordeons das seções principais:
 *   1. Próximos Passos Estratégicos
 *   2. Prognóstico e Cenários
 *   3. Pareceres Especializados
 *   4. Minuta (Edição) - Artefato principal com editor integrado
 * - Botão proeminente para iniciar nova análise
 * - Design responsivo e consistente com a identidade visual da aplicação
 * - Todos os acordeons abertos por padrão para facilitar acesso ao conteúdo
 * 
 * IMPLEMENTAÇÃO (TAREFA-082):
 * - Layout redesenhado com cards visuais distintos
 * - Hierarquia visual clara com badges numerados e ícones contextuais
 * - Espaçamento e margens otimizados para leitura
 * - Botão de "Iniciar Nova Análise" destacado e com maior apelo visual
 * - Gradientes e sombras suaves para profundidade
 * 
 * SUBCOMPONENTES INTEGRADOS:
 * - TAREFA-053: Próximos Passos (ComponenteProximosPassos) ✅
 * - TAREFA-054: Gráfico de Prognóstico (ComponenteGraficoPrognostico) ✅
 * - TAREFA-055: Pareceres Individualizados (ComponentePareceresIndividualizados) ✅
 * - TAREFA-102: Minuta (Edição) dentro do Acordeon 4 (ComponenteEditorMinuta + SeletorCenarios) ✅
 * - TAREFA-106: Atualização de rótulos com foco em Minuta como artefato principal ✅
 */
function EtapaResultados({
  resultado,
  processoId,
  onNovaAnalise,
}: {
  resultado: ResultadoAnaliseProcesso;
  processoId?: string;
  onNovaAnalise: () => void;
}) {
  // Estado local para controlar a minuta selecionada na UI de edição
  const [idMinutaSelecionada, setIdMinutaSelecionada] = useState<string | undefined>(undefined);
  return (
    <div className="mx-auto max-w-7xl space-y-8">
      {/* Header de Conclusão - Redesenhado com gradiente e mais destaque visual */}
      <div className="relative overflow-hidden rounded-2xl border border-green-200 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-8 shadow-lg">
        {/* Círculo decorativo de fundo */}
        <div className="absolute right-0 top-0 size-64 -translate-y-1/2 translate-x-1/2 rounded-full bg-green-200 opacity-20 blur-3xl"></div>

        <div className="relative z-10 text-center">
          {/* Ícone de sucesso com animação sutil */}
          <div className="mb-6 flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 animate-ping rounded-full bg-green-400 opacity-25"></div>
              <div className="relative rounded-full bg-white p-4 shadow-xl">
                <CheckCircle2 className="size-16 text-green-600" />
              </div>
            </div>
          </div>

          {/* Título e descrição */}
          <h2 className="mb-3 text-4xl font-bold text-gray-900">
            Análise Concluída com Sucesso!
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-gray-700">
            Sua minuta está pronta para edição. Confira também os pareceres especializados, prognóstico e próximos passos estratégicos
          </p>
        </div>

        {/* Banner de Contexto - Perspectiva da Defesa */}
        <div className="mb-8 rounded-r-lg border-l-4 border-blue-600 bg-gradient-to-r from-blue-50 to-indigo-50 p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="shrink-0">
              <Scale className="size-6 text-blue-600" />
            </div>
            <div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">Perspectiva da Análise</h3>
              <p className="mb-2 text-sm text-gray-700">
                Esta análise foi elaborada do ponto de vista da <strong>DEFESA</strong>.
                Seu cliente é a <strong>RECLAMADA/RÉ</strong> (a parte que está sendo processada
                pelo reclamante).
              </p>
              <ul className="list-inside list-disc space-y-1 text-sm text-gray-600">
                <li><strong>Vitória:</strong> A reclamada vence - processo julgado improcedente</li>
                <li><strong>Derrota:</strong> O reclamante vence - há condenação ao pagamento</li>
                <li><strong>Acordo:</strong> Negociação que minimize perdas para a reclamada</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Container de resultados com layout aprimorado - Minuta em destaque como acordeon 4 */}
      <div className="space-y-6">
        {/* 1. Próximos Passos Estratégicos */}
        <Accordion
          prefixo={1}
          titulo="Próximos Passos Estratégicos"
          descricao="Estratégias de defesa e próximos passos recomendados"
          defaultOpen
        >
          <ComponenteProximosPassos proximosPassos={resultado.proximos_passos} />
        </Accordion>

        {/* 2. Prognóstico e Cenários */}
        <Accordion
          prefixo={2}
          titulo="Prognóstico e Cenários"
          descricao="Probabilidades de sucesso da defesa em diferentes cenários"
          defaultOpen
        >
          <ComponenteGraficoPrognostico dados={resultado.prognostico} />
        </Accordion>

        {/* 3. Pareceres Especializados */}
        <Accordion
          prefixo={3}
          titulo="Pareceres Especializados"
          descricao="Análises detalhadas de advogados e peritos"
          defaultOpen
        >
          <ComponentePareceresIndividualizados
            pareceres_advogados={resultado.pareceres_advogados}
            pareceres_peritos={resultado.pareceres_peritos}
          />
        </Accordion>

        {/* 4. Minuta (Edição) - Artefato Principal */}
        <Accordion
          prefixo={4}
          titulo="Minuta (Edição)"
          descricao="Edite e personalize sua minuta com auxílio da IA"
          defaultOpen
        >
          {processoId ? (
            <div className="space-y-4">
              <SeletorCenarios
                processoId={processoId}
                idMinutaSelecionada={idMinutaSelecionada}
                onSelecionar={(id) => setIdMinutaSelecionada(id)}
              />
              {idMinutaSelecionada ? (
                <ComponenteEditorMinuta processoId={processoId} idMinuta={idMinutaSelecionada} />
              ) : (
                <div className="w-full rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
                  <div className="text-sm">
                    Nenhuma minuta disponível para este processo. Reprocesse a análise ou verifique os logs do backend.
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="w-full rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
              <div className="text-sm">
                Este editor de minuta requer um processo ativo. Inicie uma análise para habilitar a edição.
              </div>
            </div>
          )}
        </Accordion>
      </div>

      {/* Botão de Nova Análise - Redesenhado com mais destaque e apelo visual */}
      <div className="relative mt-12 border-t-2 border-gray-200 pt-8">
        <div className="flex flex-col items-center gap-4">
          {/* Texto motivacional acima do botão */}
          <p className="max-w-md text-center text-gray-600">
            Finalizou a edição da minuta? Inicie uma nova análise para outro processo.
          </p>

          {/* Botão principal com gradiente e sombra */}
          <button
            onClick={onNovaAnalise}
            className="group relative inline-flex items-center gap-3 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 px-10 py-4 text-lg font-semibold text-white shadow-lg transition-all duration-200 hover:scale-105 hover:from-primary-700 hover:to-primary-800 hover:shadow-xl"
          >
            {/* Ícone animado */}
            <svg className="size-6 transition-transform duration-500 group-hover:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Iniciar Nova Análise</span>

            {/* Brilho animado no hover */}
            <div className="group-hover:animate-shimmer absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20"></div>
          </button>

          {/* Texto informativo abaixo do botão */}
          <p className="text-sm text-gray-500">
            Todos os dados desta análise foram salvos automaticamente
          </p>
        </div>
      </div>

      {/* Chat IA - Widget flutuante (TAREFA-091) */}
      {processoId && (
        <ComponenteChatIA
          processoId={processoId}
          modoInicial="minimizado"
        />
      )}
    </div>
  );
}
